<script setup lang="ts">
  import { Spinner } from "@/components/ui/spinner";
  import {
    M_CAMPAIGN_PUSH_STAGE_LABELS,
    pushFailureAttemptsLabel,
  } from "@/components/MCampaign/Pushes/mCampaignPushStatus";
  import type { MCampaignPush, MCampaignPushFailure, MCampaignPushStage } from "@/types";

  const props = defineProps<{
    push: MCampaignPush;
    failures?: MCampaignPushFailure[];
  }>();

  const stages = Object.keys(M_CAMPAIGN_PUSH_STAGE_LABELS) as MCampaignPushStage[];

  const errorCodes = (failure: MCampaignPushFailure) =>
    [
      failure.code != null && `code ${failure.code}`,
      failure.subcode != null && `subcode ${failure.subcode}`,
      failure.http_status != null && `HTTP ${failure.http_status}`,
    ]
      .filter(Boolean)
      .join(" · ");
</script>

<template>
  <div class="flex flex-col gap-4 border-t bg-background px-8 py-4">
    <p
      v-if="props.push.error_message"
      class="rounded-md border border-status-error-border bg-status-error-soft px-3 py-2 text-status-error-strong">
      The push stopped: {{ props.push.error_message }}
    </p>

    <div class="grid max-w-2xl grid-cols-[minmax(0,1fr)_repeat(4,6rem)] gap-x-4 gap-y-1 text-right">
      <span class="heading-label text-left">Object</span>
      <span class="heading-label">Already synced</span>
      <span class="heading-label">Pushed</span>
      <span class="heading-label">Retries</span>
      <span class="heading-label">Errors</span>
      <template
        v-for="stage in stages"
        :key="stage">
        <span class="text-left">{{ M_CAMPAIGN_PUSH_STAGE_LABELS[stage] }}</span>
        <span class="text-muted-foreground">
          {{ props.push.counters[stage]?.sync_before ?? 0 }} / {{ props.push.counters[stage]?.total ?? 0 }}
        </span>
        <span>{{ props.push.counters[stage]?.sync_new ?? 0 }}</span>
        <span>{{ props.push.counters[stage]?.retries ?? 0 }}</span>
        <span :class="props.push.counters[stage]?.errors ? 'font-medium text-status-error-strong' : undefined">
          {{ props.push.counters[stage]?.errors ?? 0 }}
        </span>
      </template>
    </div>

    <div v-if="props.push.failures_count > 0">
      <p class="heading-label pb-2">Errors</p>
      <Spinner v-if="!props.failures" />
      <ul
        v-else
        class="flex max-h-96 flex-col divide-y overflow-auto rounded-md border bg-background"
        data-cy="m-campaign-push-failures">
        <li
          v-for="failure in props.failures"
          :key="failure.id"
          class="flex flex-col gap-0.5 px-3 py-2">
          <div class="flex items-baseline gap-2">
            <span class="text-muted-foreground">{{ M_CAMPAIGN_PUSH_STAGE_LABELS[failure.stage] }}</span>
            <span class="font-medium">{{ failure.c_object_name ?? `#${failure.c_object_id}` }}</span>
            <span
              v-if="errorCodes(failure)"
              class="ml-auto shrink-0 font-mono text-2xs text-muted-foreground">
              {{ errorCodes(failure) }}
            </span>
          </div>
          <p class="break-words text-status-error-strong">{{ failure.message }}</p>
          <p class="text-2xs text-foreground-subtle">{{ pushFailureAttemptsLabel(failure.attempts) }}</p>
        </li>
      </ul>
    </div>
  </div>
</template>
